import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRightLeft,
  BookOpen,
  Check,
  CheckCheck,
  ChevronDown,
  Circle,
  CircleCheckBig,
  CircleCheck,
  Ellipsis,
  History,
  UserCheck,
  UserPlus,
  UserRoundPlus,
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../app/layouts/MainLayout';
import { Button, cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import SearchableSelect, { SearchableSelectOption } from '../../../components/ui/SearchableSelect';
import FullScreenModal from '../../../components/ui/Modal';
import ClassLessonsPanel from '../components/ClassLessonsPanel';
import ClassManagementPanel, { ClassFormValues } from '../components/ClassManagementPanel';
import ClassStudentsPanel from '../components/ClassStudentsPanel';
import ClassTabs, { ClassTabId } from '../components/ClassTabs';
import StudentAttendanceHistoryModal from '../components/StudentAttendanceHistoryModal';
import { ClassStudentRecord } from '../mocks/classStudents';
import { useClassStudents } from '../hooks/useClassStudents';
import { useEnsureClassroom } from '../hooks/useEnsureClassroom';
import { useClassTrimesterVisitors } from '../hooks/useClassTrimesterVisitors';
import { ClassLesson } from '../types/classLessons';
import { ClassroomRecord } from '../types/classroom.types';
import { classroomService } from '../services/classroom.service';
import { classLessonService, ClassLessonRecord } from '../services/classLesson.service';
import { classVisitorService } from '../services/classVisitor.service';
import { useTeacherCandidates } from '../hooks/useTeacherCandidates';
import { AttendanceStatus } from '../../attendance/types/attendance.types';
import { useTrimesters } from '../../trimesters/context/TrimestersContext';
import { useMagazines, useSegments } from '../../trimesters/hooks';
import { buildClassLessonsFromMagazine, resolveClassMagazine } from '../utils/classLessonCatalog';
import {
  buildAttendanceEntriesFromLessons,
  buildAttendanceRateByStudentId,
  buildConsecutiveAbsencesByStudentId,
} from '../utils/classAttendanceEntries';

type StudentModalMode = 'add' | 'edit' | 'transfer';

interface StudentModalState {
  mode: StudentModalMode;
  studentId?: string;
}

interface StudentFormValues {
  source: 'member' | 'new';
  memberIds: string[];
  fullName: string;
  birthDate: string;
  phone: string;
  responsibleName: string;
  joinedEbdAt: string;
  notes: string;
  targetClassIds: string[];
}

interface LessonFormValues {
  title: string;
  date: string;
  description: string;
}

interface LessonAttendanceFormValues {
  title: string;
  date: string;
  bibles: string;
  offeringAmount: string;
  notes: string;
  studentStatuses: Record<string, AttendanceStatus>;
}

interface VisitorFormValues {
  name: string;
  whatsapp: string;
}

const buildClassFormValues = (item?: {
  title: string;
  teachers: string[];
  teacherMemberIds?: string[];
  gender: string;
  category: string;
} | null): ClassFormValues => ({
  title: item?.title ?? '',
  teacherMemberIds: item?.teacherMemberIds ?? [],
  gender: item?.gender ?? 'Mista',
  ageGroup:
    item?.category === 'Berçário'
      ? 'Berçário'
      : item?.category === 'Maternal'
        ? 'Maternal'
        : item?.category === 'Jardim de Infância'
          ? 'Jardim de Infância'
          : item?.category === 'Primários'
            ? 'Primários'
            : item?.category === 'Juniores'
              ? 'Juniores'
              : item?.category === 'Pré-Adolescentes'
                ? 'Pré-Adolescentes'
                : item?.category?.includes('Adolescentes')
                  ? 'Adolescentes'
                  : item?.category?.includes('Juvenis') || item?.category?.includes('Jovens')
                    ? 'Juvenis/Jovens'
                    : item?.category ?? 'Adultos',
});

const todayIso = '2026-04-14';

const emptyStudentFormValues: StudentFormValues = {
  source: 'member',
  memberIds: [],
  fullName: '',
  birthDate: '',
  phone: '',
  responsibleName: '',
  joinedEbdAt: todayIso,
  notes: '',
  targetClassIds: [],
};

const emptyLessonFormValues: LessonFormValues = {
  title: '',
  date: '',
  description: '',
};

const emptyClassFormValues: ClassFormValues = {
  title: '',
  teacherMemberIds: [],
  gender: 'Mista',
  ageGroup: 'Adultos',
};

const emptyVisitorFormValues: VisitorFormValues = {
  name: '',
  whatsapp: '',
};

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const calculateAge = (birthDate?: string) => {
  if (!birthDate) {
    return null;
  }

  const birth = new Date(`${birthDate}T00:00:00`);
  const today = new Date(`${todayIso}T00:00:00`);
  let age = today.getFullYear() - birth.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return Number.isFinite(age) ? age : null;
};

const formatDateLabel = (value?: string | null) => {
  if (!value) {
    return 'Não informado';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const formatCurrencyInput = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0);

const parseCurrencyInput = (value: string) => {
  const normalized = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const resolvePlannedLessonDate = (trimesterStartDate: string, lessonNumber: number) => {
  if (!trimesterStartDate || lessonNumber < 1) {
    return '';
  }

  const plannedDate = new Date(`${trimesterStartDate}T00:00:00`);
  plannedDate.setDate(plannedDate.getDate() + (lessonNumber - 1) * 7);

  return toDateInputValue(plannedDate);
};

const resolveRealCurrentTrimesterId = (
  trimesters: Array<{ id: string; startDate: string; endDate: string; active?: boolean }>,
  fallbackTrimesterId: string
) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const currentByDate = trimesters.find((trimester) => {
    const startsAt = trimester.startDate ? new Date(`${trimester.startDate}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
    const endsAt = trimester.endDate ? new Date(`${trimester.endDate}T00:00:00`).getTime() : Number.POSITIVE_INFINITY;

    return trimester.active !== false && startsAt <= today && endsAt >= today;
  });

  return currentByDate?.id ?? trimesters.find((trimester) => trimester.active)?.id ?? fallbackTrimesterId;
};

const ClassStudentsPage = () => {
  const { classId, tab } = useParams<{ classId: string; tab?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { segments } = useSegments();
  const {
    classes,
    setClasses,
    setStudents,
    getClassLessons,
    updateClass,
    removeClass,
    currentTrimesterId,
    setCurrentTrimesterId,
    trimesters,
  } = useTrimesters();
  const routeTrimesterId = searchParams.get('trimester_id') ?? '';
  const activeTrimesterId = routeTrimesterId || currentTrimesterId;
  const { magazines, isLoading: isLoadingMagazines, error: magazinesError } = useMagazines(activeTrimesterId);
  const {
    classroom: selectedClass,
    isLoadingClassroom,
    classroomError,
  } = useEnsureClassroom(classId, activeTrimesterId);

  const [transferClassrooms, setTransferClassrooms] = useState<ClassroomRecord[]>([]);
  const [classLessonRecords, setClassLessonRecords] = useState<ClassLessonRecord[]>([]);
  const realCurrentTrimesterId = useMemo(
    () => resolveRealCurrentTrimesterId(trimesters, currentTrimesterId),
    [currentTrimesterId, trimesters]
  );

  const reloadClassrooms = useCallback(async () => {
    try {
      const classroomItems = await classroomService.list(activeTrimesterId);
      setClasses(classroomItems);
    } catch {
      // The page can still operate with the selected class loaded by useEnsureClassroom.
    }
  }, [activeTrimesterId, setClasses]);

  const reloadTransferClassrooms = useCallback(async () => {
    try {
      const classroomItems = await classroomService.list(realCurrentTrimesterId);
      setTransferClassrooms(classroomItems);
    } catch {
      setTransferClassrooms([]);
    }
  }, [realCurrentTrimesterId]);

  const [studentModal, setStudentModal] = useState<StudentModalState | null>(null);
  const [studentFormValues, setStudentFormValues] = useState<StudentFormValues>(emptyStudentFormValues);
  const [studentFormError, setStudentFormError] = useState<string | null>(null);
  const [isSavingStudent, setIsSavingStudent] = useState(false);
  const [studentToRemoveId, setStudentToRemoveId] = useState<string | null>(null);
  const [studentRemoveError, setStudentRemoveError] = useState<string | null>(null);
  const [historyStudentId, setHistoryStudentId] = useState<string | null>(null);
  const [visitorFormValues, setVisitorFormValues] = useState<VisitorFormValues>(emptyVisitorFormValues);
  const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
  const [visitorHistoryId, setVisitorHistoryId] = useState<string | null>(null);
  const [visitorToConvertId, setVisitorToConvertId] = useState<string | null>(null);
  const [historyPersonName, setHistoryPersonName] = useState<string | null>(null);
  const [attendanceHistoryStudentId, setAttendanceHistoryStudentId] = useState<string | null>(null);
  const [isClassSelectorOpen, setIsClassSelectorOpen] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isClassEditOpen, setIsClassEditOpen] = useState(false);
  const { teacherCandidates } = useTeacherCandidates(isClassEditOpen);
  const [classToRemoveId, setClassToRemoveId] = useState<string | null>(null);
  const [classFormValues, setClassFormValues] = useState<ClassFormValues>(emptyClassFormValues);
  const [classFormError, setClassFormError] = useState<string | null>(null);
  const [classRemoveError, setClassRemoveError] = useState<string | null>(null);
  const [lessonModalId, setLessonModalId] = useState<string | null>(null);
  const [lessonFormValues, setLessonFormValues] = useState<LessonFormValues>(emptyLessonFormValues);
  const [attendanceLessonId, setAttendanceLessonId] = useState<string | null>(null);
  const [lessonAttendanceFormValues, setLessonAttendanceFormValues] = useState<LessonAttendanceFormValues>({
    title: '',
    date: todayIso,
    bibles: '0',
    offeringAmount: formatCurrencyInput(0),
    notes: '',
    studentStatuses: {},
  });

  useEffect(() => {
    if (!routeTrimesterId || routeTrimesterId === currentTrimesterId) {
      return;
    }

    const trimesterExists = trimesters.some((trimester) => trimester.id === routeTrimesterId);
    if (trimesterExists) {
      setCurrentTrimesterId(routeTrimesterId);
    }
  }, [currentTrimesterId, routeTrimesterId, setCurrentTrimesterId, trimesters]);

  useEffect(() => {
    reloadClassrooms();
  }, [reloadClassrooms]);

  useEffect(() => {
    reloadTransferClassrooms();
  }, [reloadTransferClassrooms]);

  useEffect(() => {
    let mounted = true;

    if (!selectedClass?.id) {
      setClassLessonRecords([]);
      return () => {
        mounted = false;
      };
    }

    classLessonService
      .listByClass(selectedClass.id, activeTrimesterId)
      .then((items) => {
        if (mounted) {
          setClassLessonRecords(items);
        }
      })
      .catch(() => {
        if (mounted) {
          setClassLessonRecords([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeTrimesterId, selectedClass?.id]);

  const {
    students: classStudentRecords,
    memberCandidates,
    isLoading: isLoadingStudents,
    isLoadingMemberCandidates,
    error: studentsError,
    reloadStudents,
    reloadMemberCandidates,
    createStudent: createClassStudent,
    updateStudent: updateClassStudent,
    transferStudent: transferClassStudent,
    removeStudent: removeClassStudent,
  } = useClassStudents(selectedClass?.id, activeTrimesterId);
  const studentRecords = useMemo(() => classStudentRecords, [classStudentRecords]);
  const activeTab: ClassTabId = tab === 'licoes' ? 'licoes' : 'alunos';
  const teacherOptions = teacherCandidates.map((teacher) => ({
    value: teacher.id,
    label: teacher.name,
    description: teacher.role,
  }));
  const segmentOptions = segments.map((segment) => ({
    value: segment.name,
    label: segment.name,
    description: `${segment.ageStart} a ${segment.ageEnd} anos`,
  }));

  const studentSearchValue = searchParams.get('q') ?? '';
  const updateFilterParams = (updates: Record<string, string>) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === '0') {
        nextParams.delete(key);
        return;
      }

      nextParams.set(key, value);
    });

    setSearchParams(nextParams);
  };

  const selectedClassStudents = useMemo<ClassStudentRecord[]>(() => {
    if (!selectedClass) {
      return [];
    }

    return studentRecords.filter((student) => student.currentClassId === selectedClass.id);
  }, [selectedClass, studentRecords]);

  const expectedAgeRange = useMemo(() => {
    if (!selectedClass) {
      return null;
    }

    const classLabel = normalizeText(`${selectedClass.category} ${selectedClass.ageRange}`);
    const matchedSegment = segments.find((segment) => classLabel.includes(normalizeText(segment.name)));

    if (matchedSegment) {
      return {
        label: `${matchedSegment.ageStart} a ${matchedSegment.ageEnd} anos`,
        start: matchedSegment.ageStart,
        end: matchedSegment.ageEnd,
      };
    }

    const plusMatch = selectedClass.ageRange.match(/(\d+)\s*\+/);
    if (plusMatch) {
      return { label: selectedClass.ageRange, start: Number(plusMatch[1]), end: 120 };
    }

    const rangeMatch = selectedClass.ageRange.match(/(\d+)\D+(\d+)/);
    if (rangeMatch) {
      return { label: selectedClass.ageRange, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
    }

    return null;
  }, [selectedClass, segments]);

  const getAgeMismatchLabel = useCallback((student: ClassStudentRecord) => {
    const age = calculateAge(student.birthDate);

    if (!expectedAgeRange || age === null || (age >= expectedAgeRange.start && age <= expectedAgeRange.end)) {
      return null;
    }

    return `Fora da faixa etária: ${age} anos; esperado ${expectedAgeRange.label}`;
  }, [expectedAgeRange]);

  const selectedClassMagazine = useMemo(
    () => resolveClassMagazine(selectedClass, activeTrimesterId, magazines, segments),
    [activeTrimesterId, magazines, segments, selectedClass]
  );
  const classLessons = useMemo<ClassLesson[]>(() => {
    if (!selectedClass || isLoadingMagazines) {
      return [];
    }

    return buildClassLessonsFromMagazine(selectedClass, selectedClassMagazine, classLessonRecords);
  }, [classLessonRecords, isLoadingMagazines, selectedClass, selectedClassMagazine]);

  const classAttendanceEntries = useMemo(
    () => (selectedClass ? buildAttendanceEntriesFromLessons(selectedClass.id, classLessons) : []),
    [classLessons, selectedClass]
  );

  const trimesterAttendanceRateByStudentId = useMemo(
    () => buildAttendanceRateByStudentId(classAttendanceEntries),
    [classAttendanceEntries]
  );

  const consecutiveAbsencesByStudentId = useMemo(
    () => buildConsecutiveAbsencesByStudentId(classAttendanceEntries),
    [classAttendanceEntries]
  );

  const classStats = useMemo(() => {
    if (selectedClassStudents.length === 0) {
      return { students: 0, avg: '0%' };
    }

    const attendanceSum = selectedClassStudents.reduce(
      (sum, student) => sum + (trimesterAttendanceRateByStudentId[student.id] ?? student.attendanceRate),
      0
    );

    return {
      students: selectedClassStudents.length,
      avg: `${Math.round(attendanceSum / selectedClassStudents.length)}%`,
    };
  }, [selectedClassStudents, trimesterAttendanceRateByStudentId]);

  const filteredClassStudents = useMemo(() => {
    return selectedClassStudents
      .filter((student) => {
        const normalizedTerm = studentSearchValue.trim().toLowerCase();
        const matchesSearch =
          normalizedTerm.length === 0 ||
          student.name.toLowerCase().includes(normalizedTerm) ||
          student.notes.toLowerCase().includes(normalizedTerm) ||
          student.ageGroup.toLowerCase().includes(normalizedTerm);

        return matchesSearch;
      })
      .map((student) => ({
        id: student.id,
        name: student.name,
        ageGroup: student.ageGroup,
        ageLabel: student.ageLabel,
        joinedEbdAtLabel: formatDateLabel(student.joinedEbdAt),
        attendanceRate: trimesterAttendanceRateByStudentId[student.id] ?? student.attendanceRate,
        attendanceLabel:
          (consecutiveAbsencesByStudentId[student.id] ?? student.consecutiveAbsences) >= 3
            ? `${consecutiveAbsencesByStudentId[student.id] ?? student.consecutiveAbsences} faltas consecutivas`
            : `${trimesterAttendanceRateByStudentId[student.id] ?? student.attendanceRate}% de presença`,
        classHistoryCount: student.classHistory.length,
        recurringAbsentee: (consecutiveAbsencesByStudentId[student.id] ?? student.consecutiveAbsences) >= 3,
        lowAttendance: (trimesterAttendanceRateByStudentId[student.id] ?? student.attendanceRate) < 35,
        notes: student.notes,
        consecutiveAbsences: consecutiveAbsencesByStudentId[student.id] ?? student.consecutiveAbsences,
        ageMismatchLabel: getAgeMismatchLabel(student) ?? undefined,
        ageMismatch: Boolean(getAgeMismatchLabel(student)),
      }));
  }, [consecutiveAbsencesByStudentId, getAgeMismatchLabel, selectedClassStudents, studentSearchValue, trimesterAttendanceRateByStudentId]);
  const activeTrimester = useMemo(
    () => trimesters.find((trimester) => trimester.id === activeTrimesterId) ?? null,
    [activeTrimesterId, trimesters]
  );

  const lessonsView = useMemo(() => {
    return classLessons.map((lesson) => {
      const presentCount = lesson.presentCount ?? 0;
      const enrolledCount = selectedClassStudents.length;
      const isConfigured = Boolean(lesson.title.trim() || lesson.date || lesson.description.trim());
      const plannedDate = resolvePlannedLessonDate(activeTrimester?.startDate ?? '', lesson.lessonNumber);
      const displayDate = lesson.date || plannedDate;
      const displayDateKind: 'held' | 'planned' | null = displayDate ? (lesson.date ? 'held' : 'planned') : null;

      return {
        ...lesson,
        displayDate,
        displayDateKind,
        hasAttendance: lesson.hasAttendance ?? false,
        isConfigured,
        attendanceSummaryLabel: lesson.hasAttendance ? `${presentCount}/${enrolledCount || 0} presentes` : '',
      };
    });
  }, [activeTrimester?.startDate, classLessons, selectedClassStudents.length]);

  const classLessonIds = useMemo(() => classLessons.map((lesson) => lesson.id), [classLessons]);
  const {
    classVisitors: selectedClassVisitors,
    reloadVisitors,
  } = useClassTrimesterVisitors(selectedClass?.id, classLessonIds, activeTrimesterId);

  const classVisitorStats = useMemo(() => {
    const totalVisits = selectedClassVisitors.reduce((sum, visitor) => sum + visitor.visits.length, 0);
    return {
      registered: selectedClassVisitors.length,
      visits: totalVisits,
    };
  }, [selectedClassVisitors]);

  const summaryMetrics = useMemo(() => {
    const recurringAbsenteesCount = selectedClassStudents.filter((student) => student.consecutiveAbsences >= 3).length;
    const lessonsCompleted = lessonsView.filter((lesson) => lesson.hasAttendance).length;

    return {
      recurringAbsenteesCount,
      lessonsCompleted,
    };
  }, [lessonsView, selectedClassStudents]);

  const masterMemberOptions: SearchableSelectOption[] = useMemo(() => {
    return memberCandidates.map((member) => ({
      value: member.id,
      label: member.name,
      description: `${member.role ?? 'Membro'} • ${member.activeStudent?.className ?? 'Sem turma EBD ativa'}`,
    }));
  }, [memberCandidates]);

  const targetClassOptions: SearchableSelectOption[] = useMemo(() => {
    return transferClassrooms
      .filter((item) => item.id !== selectedClass?.id && item.active !== false)
      .map((item) => ({
        value: item.id,
        label: item.title,
        description: `${item.category} • ${item.room}`,
      }));
  }, [selectedClass, transferClassrooms]);

  const visitorsView = useMemo(() => {
    return selectedClassVisitors
      .map((visitor) => {
        const orderedVisits = visitor.visits.slice().sort((left, right) => right.date.localeCompare(left.date));
        const lastVisit = orderedVisits[0];

        return {
          id: visitor.id,
          name: visitor.name,
          whatsapp: visitor.whatsapp,
          visitCount: visitor.visits.length,
          lastVisitLabel: lastVisit ? `Última visita: ${formatDateLabel(lastVisit.date)}` : 'Sem visitas registradas',
          convertedToStudentId: visitor.convertedToStudentId,
        };
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [selectedClassVisitors]);

  const historyStudent = historyStudentId ? studentRecords.find((student) => student.id === historyStudentId) ?? null : null;
  const historyVisitor = visitorHistoryId
    ? selectedClassVisitors.find((visitor) => visitor.id === visitorHistoryId) ?? null
    : null;
  const visitorToConvert = visitorToConvertId
    ? selectedClassVisitors.find((visitor) => visitor.id === visitorToConvertId) ?? null
    : null;
  const historyPersonStudent = historyPersonName
    ? studentRecords.find((student) => student.name === historyPersonName) ?? null
    : null;
  const historyPersonTeacherClasses = useMemo(
    () => (historyPersonName ? classes.filter((item) => item.teachers.includes(historyPersonName)) : []),
    [classes, historyPersonName]
  );
  const studentToRemove = studentToRemoveId ? studentRecords.find((student) => student.id === studentToRemoveId) ?? null : null;
  const attendanceHistoryStudent = attendanceHistoryStudentId
    ? studentRecords.find((student) => student.id === attendanceHistoryStudentId) ?? null
    : null;
  const editingLesson = lessonModalId ? classLessons.find((lesson) => lesson.id === lessonModalId) ?? null : null;
  const attendanceLesson = attendanceLessonId ? classLessons.find((lesson) => lesson.id === attendanceLessonId) ?? null : null;
  const classToRemove = classToRemoveId ? classes.find((item) => item.id === classToRemoveId) ?? null : null;

  const closeStudentModal = () => {
    setStudentModal(null);
    setStudentFormValues(emptyStudentFormValues);
    setStudentFormError(null);
    setIsSavingStudent(false);
  };

  const closeVisitorModal = () => {
    setIsVisitorModalOpen(false);
    setVisitorFormValues(emptyVisitorFormValues);
  };

  const closeLessonModal = () => {
    setLessonModalId(null);
    setLessonFormValues(emptyLessonFormValues);
  };

  const closeAttendanceModal = () => {
    setAttendanceLessonId(null);
    setLessonAttendanceFormValues({
      title: '',
      date: todayIso,
      bibles: '0',
      offeringAmount: formatCurrencyInput(0),
      notes: '',
      studentStatuses: {},
    });
  };

  const openEditClassModal = () => {
    setClassFormValues(buildClassFormValues(selectedClass));
    setClassFormError(null);
    setIsClassEditOpen(true);
    setIsActionsMenuOpen(false);
  };

  const closeEditClassModal = () => {
    setIsClassEditOpen(false);
    setClassFormValues(emptyClassFormValues);
    setClassFormError(null);
  };

  const handleChangeClassValue = (field: keyof ClassFormValues, value: string | string[]) => {
    setClassFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleSaveClassDetails = async () => {
    if (!selectedClass || !classFormValues.title.trim()) {
      setClassFormError('Informe o nome da turma.');
      return;
    }

    const selectedTeacherIds = classFormValues.teacherMemberIds.filter((teacherId) => teacherId.trim().length > 0);
    const selectedTeacherNames = teacherCandidates
      .filter((teacher) => selectedTeacherIds.includes(teacher.id))
      .map((teacher) => teacher.name);
    const selectedSegment = segments.find((segment) => segment.name === classFormValues.ageGroup) ?? null;

    try {
      const savedClass = await classroomService.update(selectedClass.id, {
        title: classFormValues.title.trim(),
        teacherNames: selectedTeacherNames,
        teacherMemberIds: selectedTeacherIds,
        gender: classFormValues.gender,
        segmentId: selectedSegment?.id ?? null,
        trimesterId: activeTrimesterId,
      });

      updateClass(selectedClass.id, savedClass);
      closeEditClassModal();
    } catch (saveError) {
      setClassFormError(saveError instanceof Error ? saveError.message : 'Nao foi possivel salvar a turma.');
    }
  };

  const handleConfirmRemoveClass = async () => {
    if (!classToRemove) {
      return;
    }

    try {
      await classroomService.remove(classToRemove.id);
      setStudents((current) =>
        current.map((student) => {
          if (student.currentClassId !== classToRemove.id) {
            return student;
          }

          const history = [...student.classHistory];
          const openEntryIndex = history.findIndex((entry) => entry.classId === classToRemove.id && !entry.leftAt);

          if (openEntryIndex >= 0) {
            history[openEntryIndex] = {
              ...history[openEntryIndex],
              leftAt: todayIso,
              reason: 'Turma removida da estrutura',
            };
          }

          return { ...student, currentClassId: null, classHistory: history };
        })
      );
      removeClass(classToRemove.id);
      setClassToRemoveId(null);
      setClassRemoveError(null);
      navigate('/classes');
    } catch (deleteError) {
      setClassRemoveError(deleteError instanceof Error ? deleteError.message : 'Nao foi possivel deletar a turma.');
    }
  };

  const handleSaveLesson = () => {
    if (!selectedClass || !lessonModalId) {
      return;
    }

    setClassLessonRecords((current) =>
      current.map((lesson) =>
        lesson.id === lessonModalId
          ? {
              ...lesson,
              title: lessonFormValues.title.trim(),
              date: lessonFormValues.date,
              description: lessonFormValues.description.trim(),
            }
          : lesson
      )
    );
    closeLessonModal();
  };

  const openLessonReport = (lessonId: string) => {
    if (!selectedClass) {
      return;
    }

    navigate(`/relatorios/by-lesson?class_id=${selectedClass.id}&lesson_id=${lessonId}&trimester_id=${activeTrimesterId}`);
  };

  const handleAttendanceStatusChange = (studentId: string, status: AttendanceStatus) => {
    setLessonAttendanceFormValues((current) => ({
      ...current,
      studentStatuses: {
        ...current.studentStatuses,
        [studentId]: status,
      },
    }));
  };

  const handleFillAttendance = (status: AttendanceStatus) => {
    setLessonAttendanceFormValues((current) => ({
      ...current,
      studentStatuses: selectedClassStudents.reduce<Record<string, AttendanceStatus>>((accumulator, student) => {
        accumulator[student.id] = status;
        return accumulator;
      }, {}),
    }));
  };

  const handleSaveLessonAttendance = async () => {
    if (!selectedClass || !attendanceLesson) {
      return;
    }

    const normalizedLessonDate = lessonAttendanceFormValues.date || todayIso;
    const normalizedBibles = Math.floor(Math.max(0, Number(lessonAttendanceFormValues.bibles) || 0));
    const normalizedOfferingAmount = parseCurrencyInput(lessonAttendanceFormValues.offeringAmount);

    try {
      const savedLesson = await classLessonService.syncAttendance(selectedClass.id, attendanceLesson.id, {
        trimesterId: activeTrimesterId,
        date: normalizedLessonDate,
        description: attendanceLesson.description,
        biblesCount: normalizedBibles,
        offeringAmount: normalizedOfferingAmount,
        notes: lessonAttendanceFormValues.notes.trim(),
        attendances: selectedClassStudents.map((student) => ({
          studentId: student.id,
          present: (lessonAttendanceFormValues.studentStatuses[student.id] ?? 'absent') === 'present',
        })),
      });

      setClassLessonRecords((current) => {
        const filtered = current.filter((lesson) => lesson.id !== savedLesson.id);
        return [...filtered, savedLesson].sort((left, right) => left.lessonNumber - right.lessonNumber);
      });
      closeAttendanceModal();
    } catch (saveError) {
      setStudentFormError(saveError instanceof Error ? saveError.message : 'Nao foi possivel salvar a frequência.');
    }
  };

  const getVisitorsCountForDate = (date: string) =>
    selectedClassVisitors.reduce(
      (sum, visitor) => sum + visitor.visits.filter((visit) => visit.classId === selectedClass?.id && visit.date === date).length,
      0
    );

  const handleSaveVisitor = async () => {
    if (!selectedClass || !visitorFormValues.name.trim() || !visitorFormValues.whatsapp.trim()) {
      return;
    }

    const targetLesson = classLessons.find((lesson) => lesson.date === todayIso) ?? classLessons[0] ?? null;

    if (!targetLesson) {
      return;
    }

    await classVisitorService.createForLesson(selectedClass.id, targetLesson.id, {
      trimesterId: activeTrimesterId,
      date: targetLesson.date || todayIso,
      name: visitorFormValues.name,
      whatsapp: visitorFormValues.whatsapp,
      notes: 'Cadastro pela listagem de alunos.',
    });
    await reloadVisitors();
    closeVisitorModal();
  };

  const handleConfirmConvertVisitor = async () => {
    if (!visitorToConvert || !selectedClass) {
      return;
    }

    if (visitorToConvert.convertedToStudentId) {
      setVisitorToConvertId(null);
      return;
    }

    const sourceVisit = visitorToConvert.visits.find((visit) => visit.lessonId) ?? visitorToConvert.visits[0];

    if (!sourceVisit?.lessonId) {
      setVisitorToConvertId(null);
      return;
    }

    await classVisitorService.convertToStudent(
      selectedClass.id,
      sourceVisit.lessonId,
      activeTrimesterId,
      visitorToConvert.id,
      todayIso
    );
    await reloadVisitors();
    await reloadStudents();
    setVisitorToConvertId(null);
  };

  const openAddStudentModal = () => {
    if (!selectedClass) {
      return;
    }

    setStudentModal({ mode: 'add' });
    setStudentFormValues({ ...emptyStudentFormValues, joinedEbdAt: todayIso });
    setStudentFormError(null);
    reloadMemberCandidates();
  };

  const openEditStudentModal = (studentId: string) => {
    const student = studentRecords.find((item) => item.id === studentId);

    if (!student) {
      return;
    }

    setStudentModal({ mode: 'edit', studentId });
    setStudentFormValues({
      source: 'new',
      memberIds: [student.id],
      fullName: student.name,
      birthDate: student.birthDate ?? '',
      phone: '',
      responsibleName: '',
      joinedEbdAt: student.joinedEbdAt,
      notes: student.notes,
      targetClassIds: [],
    });
    setStudentFormError(null);
  };

  const openTransferStudentModal = (studentId: string) => {
    const student = studentRecords.find((item) => item.id === studentId);

    if (!student) {
      return;
    }

    setStudentModal({ mode: 'transfer', studentId });
    setStudentFormValues({
      ...emptyStudentFormValues,
      memberIds: [student.id],
      fullName: student.name,
      birthDate: student.birthDate ?? '',
      joinedEbdAt: student.joinedEbdAt,
      notes: student.notes,
      targetClassIds: [],
    });
    setStudentFormError(null);
  };

  const handleMemberSelection = (values: string[]) => {
    const nextMemberId = values.slice(-1);
    const member = memberCandidates.find((item) => item.id === nextMemberId[0]);

    setStudentFormValues((current) => ({
      ...current,
      memberIds: nextMemberId,
      fullName: member?.name ?? current.fullName,
      birthDate: member?.birthDate ?? current.birthDate,
      phone: member?.phone ?? current.phone,
    }));
  };

  const handleSubmitStudentModal = async () => {
    if (!studentModal) {
      return;
    }

    setStudentFormError(null);
    setIsSavingStudent(true);

    try {
    if (studentModal.mode === 'add') {
      const selectedMemberId = studentFormValues.memberIds[0];

      if (!selectedClass) {
        return;
      }

      if (studentFormValues.source === 'member') {
        if (!selectedMemberId) {
          setStudentFormError('Selecione um membro para vincular.');
          return;
        }

        await createClassStudent({
          memberId: selectedMemberId,
          enrolledAt: studentFormValues.joinedEbdAt,
          notes: studentFormValues.notes,
        });
      } else {
        if (!studentFormValues.fullName.trim()) {
          setStudentFormError('Informe o nome do aluno.');
          return;
        }

        await createClassStudent({
          fullName: studentFormValues.fullName,
          birthDate: studentFormValues.birthDate,
          phone: studentFormValues.phone,
          responsibleName: studentFormValues.responsibleName,
          enrolledAt: studentFormValues.joinedEbdAt,
          notes: studentFormValues.notes,
        });
      }

      await reloadClassrooms();
      closeStudentModal();
      return;
    }

    if (studentModal.mode === 'edit' && studentModal.studentId) {
      await updateClassStudent(studentModal.studentId, {
        fullName: studentFormValues.fullName,
        birthDate: studentFormValues.birthDate,
        phone: studentFormValues.phone,
        responsibleName: studentFormValues.responsibleName,
        enrolledAt: studentFormValues.joinedEbdAt,
        notes: studentFormValues.notes,
      });
      closeStudentModal();
      return;
    }

    if (studentModal.mode === 'transfer' && studentModal.studentId) {
      const targetClassId = studentFormValues.targetClassIds[0];

      if (!targetClassId) {
        setStudentFormError('Selecione a turma de destino.');
        return;
      }

      const targetClass = transferClassrooms.find((item) => item.id === targetClassId);

      if (!targetClass) {
        setStudentFormError('Turma de destino não encontrada.');
        return;
      }

      await transferClassStudent(studentModal.studentId, targetClass.id);
      await reloadClassrooms();
      await reloadTransferClassrooms();

      closeStudentModal();
    }
    } catch (saveError) {
      setStudentFormError(saveError instanceof Error ? saveError.message : 'Nao foi possivel salvar o aluno.');
    } finally {
      setIsSavingStudent(false);
    }
  };

  const handleConfirmRemoveStudent = async () => {
    if (!studentToRemoveId) {
      return;
    }

    try {
      setStudentRemoveError(null);
      await removeClassStudent(studentToRemoveId);
      await reloadClassrooms();
      setStudentToRemoveId(null);
    } catch (removeError) {
      setStudentRemoveError(removeError instanceof Error ? removeError.message : 'Nao foi possivel remover o aluno.');
    }
  };

  const studentModalTitle =
    studentModal?.mode === 'add'
      ? 'Vincular aluno à turma'
      : studentModal?.mode === 'edit'
        ? 'Editar vínculo do aluno'
        : 'Transferir aluno de turma';

  const editingStudent = studentModal?.studentId
    ? studentRecords.find((student) => student.id === studentModal.studentId) ?? null
    : null;
  const lessonAttendanceSummary = selectedClassStudents.reduce(
    (accumulator, student) => {
      const status = lessonAttendanceFormValues.studentStatuses[student.id] ?? 'absent';

      if (status === 'present') {
        accumulator.present += 1;
      } else {
        accumulator.absent += 1;
      }

      return accumulator;
    },
    { present: 0, absent: 0 }
  );
  const lessonAttendanceBibles = Math.max(0, Number(lessonAttendanceFormValues.bibles) || 0);
  const lessonAttendanceOffering = parseCurrencyInput(lessonAttendanceFormValues.offeringAmount);
  const lessonAttendanceVisitors = getVisitorsCountForDate(lessonAttendanceFormValues.date || todayIso);

  const getProjectedAbsenceStreak = (student: ClassStudentRecord) => {
    const currentStatus = lessonAttendanceFormValues.studentStatuses[student.id] ?? 'absent';
    return currentStatus === 'present' ? 0 : student.consecutiveAbsences + 1;
  };

  const attendanceHistoryEntries = useMemo(() => {
    if (!attendanceHistoryStudent) {
      return [];
    }

    return classAttendanceEntries
      .filter((entry) => entry.studentStatuses.some((item) => item.studentId === attendanceHistoryStudent.id))
      .map((entry) => {
        const lesson = classLessons.find((item) => item.id === entry.lessonId) ?? null;
        const studentStatus = entry.studentStatuses.find((item) => item.studentId === attendanceHistoryStudent.id)?.status ?? 'absent';
        const presentCount = entry.studentStatuses.filter((item) => item.status === 'present').length;

        return {
          id: entry.id,
          date: entry.date,
          lessonTitle: lesson?.title || `Aula de ${formatDateLabel(entry.date)}`,
          lessonNumber: lesson?.lessonNumber ?? null,
          notes: entry.notes,
          status: studentStatus,
          presentCount,
          enrolledCount: entry.studentStatuses.length,
        };
      })
      .sort((left, right) => right.date.localeCompare(left.date));
  }, [attendanceHistoryStudent, classAttendanceEntries, classLessons]);

  const attendanceHistorySummary = useMemo(() => {
    return attendanceHistoryEntries.reduce(
      (accumulator, entry) => {
        if (entry.status === 'present') {
          accumulator.present += 1;
        } else {
          accumulator.absent += 1;
        }

        return accumulator;
      },
      { present: 0, absent: 0 }
    );
  }, [attendanceHistoryEntries]);

  const teacherParticipationEntries = useMemo(() => {
    if (!historyPersonName) {
      return [];
    }

    const teacherClassIds = new Set(historyPersonTeacherClasses.map((item) => item.id));

    return classAttendanceEntries
      .filter((entry) => teacherClassIds.has(entry.classId))
      .map((entry) => {
        const teacherClass = classes.find((item) => item.id === entry.classId);
        const lesson = getClassLessons(entry.classId, activeTrimesterId).find((item) => item.id === entry.lessonId) ?? null;

        return {
          id: entry.id,
          date: entry.date,
          className: teacherClass?.title ?? 'Turma',
          lessonTitle: lesson?.title || `Aula de ${formatDateLabel(entry.date)}`,
          lessonNumber: lesson?.lessonNumber ?? null,
          presentCount: entry.studentStatuses.filter((item) => item.status === 'present').length,
          enrolledCount: entry.studentStatuses.length,
          notes: entry.notes,
        };
      })
      .sort((left, right) => right.date.localeCompare(left.date));
  }, [activeTrimesterId, classAttendanceEntries, classes, getClassLessons, historyPersonName, historyPersonTeacherClasses]);

  const buildClassUrl = (nextClassId: string, nextTab: ClassTabId = activeTab) =>
    `/classes/${nextClassId}/${nextTab}?trimester_id=${activeTrimesterId}`;

  const buildLessonUrl = (lessonId: string) =>
    `/classes/${selectedClass?.id}/licoes/${lessonId}?trimester_id=${activeTrimesterId}`;

  const handleTabChange = (nextTab: ClassTabId) => {
    if (!selectedClass) {
      return;
    }

    navigate(buildClassUrl(selectedClass.id, nextTab));
  };

  if (isLoadingClassroom) {
    return (
      <MainLayout showBack onBack={() => navigate('/classes')}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
          Carregando turma...
        </div>
      </MainLayout>
    );
  }

  if (!selectedClass) {
    return (
      <MainLayout showBack onBack={() => navigate('/classes')}>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{classroomError ?? 'Turma não encontrada.'}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showBack onBack={() => navigate('/classes')}>
      <div className="space-y-5">
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-2xl font-bold text-slate-900">{selectedClass.title}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {classStats.students} {classStats.students === 1 ? 'aluno' : 'alunos'} • {classVisitorStats.registered}{' '}
                {classVisitorStats.registered === 1 ? 'visitante' : 'visitantes'} • {classStats.avg} de presença
              </p>
            </div>
            <div className="flex items-center gap-3 self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setIsClassSelectorOpen(true)}
                className="flex h-12 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm"
              >
                <BookOpen size={16} className="text-primary" />
                <span className="max-w-[180px] truncate">{selectedClass.title}</span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>

              <div className="relative shrink-0">
                <Button className="h-12 gap-2 px-4" onClick={() => setIsActionsMenuOpen((current) => !current)}>
                  <Ellipsis size={18} />
                  Ações
                </Button>
                {isActionsMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+8px)] z-20 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    <button
                      type="button"
                      onClick={() => {
                        setIsActionsMenuOpen(false);
                        openAddStudentModal();
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <UserPlus size={16} className="text-primary" />
                      VINCULAR ALUNO
                    </button>
                    <button
                      type="button"
                      onClick={openEditClassModal}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <CircleCheck size={16} className="text-sky-600" />
                      Editar turma
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setClassToRemoveId(selectedClass.id);
                        setClassRemoveError(null);
                        setIsActionsMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50"
                    >
                      <AlertTriangle size={16} />
                      Deletar turma
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ClassTabs activeTab={activeTab} onChange={handleTabChange} />
        </div>

        {activeTab === 'alunos' && (
          <>
            {studentsError && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-medium text-rose-800">
                {studentsError}
              </div>
            )}
            {isLoadingStudents && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
                Carregando alunos...
              </div>
            )}
            <ClassStudentsPanel
              items={filteredClassStudents}
              visitors={visitorsView}
              searchValue={studentSearchValue}
              onSearchChange={(value) => updateFilterParams({ q: value })}
              onClearFilters={() => updateFilterParams({ q: '', age_group: '' })}
              onOpenAttendanceHistory={(studentId) => setAttendanceHistoryStudentId(studentId)}
              onEditStudent={openEditStudentModal}
              onTransferStudent={openTransferStudentModal}
              onRemoveStudent={(studentId) => {
                setStudentRemoveError(null);
                setStudentToRemoveId(studentId);
              }}
              onViewHistory={(studentId) => setHistoryStudentId(studentId)}
              onViewVisitorHistory={(visitorId) => setVisitorHistoryId(visitorId)}
              onConvertVisitor={(visitorId) => setVisitorToConvertId(visitorId)}
            />
          </>
        )}

        {activeTab === 'licoes' && magazinesError && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-medium text-rose-800">
            {magazinesError}
          </div>
        )}

        {activeTab === 'licoes' && isLoadingMagazines ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
            Carregando lições do trimestre...
          </div>
        ) : activeTab === 'licoes' ? (
          <ClassLessonsPanel
            studentsCount={classStats.students}
            visitorsCount={classVisitorStats.registered}
            attendanceAverage={classStats.avg}
            lessonsCompleted={summaryMetrics.lessonsCompleted}
            recurringAbsenteesCount={summaryMetrics.recurringAbsenteesCount}
            lessons={lessonsView}
            onOpenLessonDetails={(lessonId) => navigate(buildLessonUrl(lessonId))}
            onOpenLessonReport={openLessonReport}
          />
        ) : null}
      </div>

      <FullScreenModal
        open={isClassSelectorOpen}
        onClose={() => setIsClassSelectorOpen(false)}
        title="Selecionar turma"
        subtitle="Escolha a turma para visualizar alunos e lições."
      >
        <div className="space-y-3">
          {classes.map((item) => {
            const isActive = item.id === selectedClass.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  navigate(buildClassUrl(item.id));
                  setIsClassSelectorOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors',
                  isActive ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
              >
                <div>
                  <p className="text-base font-semibold text-slate-900">{item.title}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {item.category} • {item.room}
                  </p>
                </div>
                {isActive && (
                  <span className="rounded-full bg-primary p-1.5 text-white">
                    <Check size={14} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </FullScreenModal>

      <FullScreenModal open={isClassEditOpen} onClose={closeEditClassModal} title="Editar turma">
        <ClassManagementPanel
          mode="edit"
          values={classFormValues}
          teacherOptions={teacherOptions}
          segmentOptions={segmentOptions}
          onChange={handleChangeClassValue}
          onSubmit={handleSaveClassDetails}
          errorMessage={classFormError}
        />
      </FullScreenModal>

      <FullScreenModal
        open={classToRemove !== null}
        onClose={() => {
          setClassToRemoveId(null);
          setClassRemoveError(null);
        }}
        title="Deletar turma"
        subtitle={classToRemove?.title}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              onClick={() => {
                setClassToRemoveId(null);
                setClassRemoveError(null);
              }}
            >
              Cancelar
            </Button>
            <Button className="h-12 bg-rose-600 hover:bg-rose-700" onClick={handleConfirmRemoveClass}>
              Deletar turma
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
          <AlertTriangle size={20} className="shrink-0 text-rose-600" />
          <p className="text-sm text-rose-900">
            Esta ação exclui a turma atual e redireciona você para a tela de listagem.
          </p>
        </div>
        {classRemoveError && (
          <div className="mt-3 rounded-2xl border border-rose-100 bg-white p-3 text-sm font-medium text-rose-700">
            {classRemoveError}
          </div>
        )}
      </FullScreenModal>

      <FullScreenModal
        open={editingLesson !== null}
        onClose={closeLessonModal}
        title={editingLesson ? `Lição ${editingLesson.lessonNumber}` : 'Editar lição'}
        subtitle="Cadastre o título, a data e a descrição da lição deste trimestre."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeLessonModal}>
              Cancelar
            </Button>
            <Button className="h-12" onClick={handleSaveLesson}>
              Salvar lição
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Título da lição"
            value={lessonFormValues.title}
            onChange={(event) => setLessonFormValues((current) => ({ ...current, title: event.target.value }))}
            placeholder="Ex.: O chamado para viver em santidade"
          />

          <Input
            label="Data da aula"
            type="date"
            value={lessonFormValues.date}
            onChange={(event) => setLessonFormValues((current) => ({ ...current, date: event.target.value }))}
          />

          <div className="space-y-1.5">
            <label className="ml-1 text-sm font-medium text-gray-700">Descrição da lição</label>
            <textarea
              value={lessonFormValues.description}
              onChange={(event) => setLessonFormValues((current) => ({ ...current, description: event.target.value }))}
              placeholder="Resumo rápido do conteúdo, objetivo da aula e observações úteis para o professor."
              className="min-h-[140px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={attendanceLesson !== null}
        onClose={closeAttendanceModal}
        title={attendanceLesson ? `Frequência da Lição ${attendanceLesson.lessonNumber}` : 'Frequência da aula'}
        subtitle={lessonAttendanceFormValues.title || 'Chamada individual da aula.'}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeAttendanceModal}>
              Cancelar
            </Button>
            <Button className="h-12" onClick={handleSaveLessonAttendance}>
              Salvar frequência
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_45px_-28px_rgba(15,23,42,0.18)]">
            <div className="border-b border-slate-200/80 px-4 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Registro da aula</p>
            </div>

            <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2">
              <div className="rounded-[22px] border border-white/80 bg-white/90 p-3 shadow-sm backdrop-blur">
                <Input
                  label="Título da aula"
                  value={lessonAttendanceFormValues.title}
                  onChange={(event) =>
                    setLessonAttendanceFormValues((current) => ({ ...current, title: event.target.value }))
                  }
                  placeholder="Ex.: O chamado para viver em santidade"
                  className="border-slate-200 bg-slate-50"
                />
              </div>

              <div className="rounded-[22px] border border-white/80 bg-white/90 p-3 shadow-sm backdrop-blur">
                <Input
                  label="Data da aula"
                  type="date"
                  value={lessonAttendanceFormValues.date}
                  onChange={(event) =>
                    setLessonAttendanceFormValues((current) => ({ ...current, date: event.target.value || todayIso }))
                  }
                  className="border-slate-200 bg-slate-50"
                />
              </div>

              <div className="rounded-[22px] border border-white/80 bg-white/90 p-3 shadow-sm backdrop-blur">
                <Input
                  label="Quantidade de Bíblias"
                  type="number"
                  min={0}
                  step={1}
                  value={lessonAttendanceFormValues.bibles}
                  onChange={(event) => {
                    const value = event.target.value;
                    setLessonAttendanceFormValues((current) => ({
                      ...current,
                      bibles: value === '' ? '' : String(Math.max(0, Number(value) || 0)),
                    }));
                  }}
                  placeholder="0"
                  className="border-slate-200 bg-slate-50"
                />
              </div>

              <div className="rounded-[22px] border border-white/80 bg-white/90 p-3 shadow-sm backdrop-blur">
                <Input
                  label="Valor da oferta"
                  inputMode="decimal"
                  value={lessonAttendanceFormValues.offeringAmount}
                  onChange={(event) =>
                    setLessonAttendanceFormValues((current) => ({ ...current, offeringAmount: event.target.value }))
                  }
                  onBlur={() =>
                    setLessonAttendanceFormValues((current) => ({
                      ...current,
                      offeringAmount: formatCurrencyInput(parseCurrencyInput(current.offeringAmount)),
                    }))
                  }
                  placeholder="R$ 0,00"
                  className="border-slate-200 bg-slate-50"
                />
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_45px_-30px_rgba(15,23,42,0.16)]">
            <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              {lessonAttendanceFormValues.date ? (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Data da aula</p>
                  <p className="mt-1 text-xl font-bold tracking-tight text-slate-900">
                    {formatDateLabel(lessonAttendanceFormValues.date)}
                  </p>
                </div>
              ) : (
                <span />
              )}
              <div className="grid grid-cols-2 gap-3 text-right lg:grid-cols-5">
                <div className="min-w-[108px] rounded-[24px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-3xl font-bold leading-none text-slate-900">{lessonAttendanceSummary.present}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Presentes</p>
                </div>
                <div className="min-w-[108px] rounded-[24px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-3xl font-bold leading-none text-slate-900">{lessonAttendanceSummary.absent}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Faltas</p>
                </div>
                <div className="min-w-[108px] rounded-[24px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-3xl font-bold leading-none text-slate-900">{lessonAttendanceBibles}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Bíblias</p>
                </div>
                <div className="min-w-[108px] rounded-[24px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-3xl font-bold leading-none text-slate-900">{lessonAttendanceVisitors}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Visitantes</p>
                </div>
                <div className="min-w-[108px] rounded-[24px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-lg font-bold leading-none text-slate-900">
                    {formatCurrencyInput(lessonAttendanceOffering)}
                  </p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">Oferta</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="h-12 flex-1 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white text-emerald-700 shadow-sm hover:from-emerald-100 hover:to-emerald-50"
              onClick={() => handleFillAttendance('present')}
            >
              <CheckCheck size={18} />
              Marcar todos presentes
            </Button>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Chamada</p>
            </div>

            {selectedClassStudents.length > 0 ? (
              selectedClassStudents.map((student) => {
                const currentStatus = lessonAttendanceFormValues.studentStatuses[student.id] ?? 'absent';
                const isPresent = currentStatus === 'present';
                const projectedStreak = getProjectedAbsenceStreak(student);
                const trimesterAttendanceRate = trimesterAttendanceRateByStudentId[student.id] ?? student.attendanceRate;
                const hasLowAttendance = trimesterAttendanceRate < 35;
                const ageMismatchLabel = getAgeMismatchLabel(student);
                return (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => handleAttendanceStatusChange(student.id, isPresent ? 'absent' : 'present')}
                    className="w-full text-left"
                  >
                    <Card
                      className={cn(
                        'space-y-3 border-slate-200 bg-white p-4 shadow-none transition-colors',
                        hasLowAttendance && 'border-amber-200 bg-amber-50/40',
                        ageMismatchLabel && 'border-rose-200',
                        isPresent && 'border-emerald-300 bg-emerald-50/60'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{student.name}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                                {student.ageGroup}
                              </span>
                              <span
                                className={cn(
                                  'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                                  isPresent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                                )}
                              >
                                {isPresent ? 'Presente' : 'Ausente'}
                              </span>
                              {projectedStreak >= 3 && (
                                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                                  {projectedStreak} faltas consecutivas
                                </span>
                              )}
                              {hasLowAttendance && (
                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                                  Atenção: {trimesterAttendanceRate}% no trimestre
                                </span>
                              )}
                              {ageMismatchLabel && (
                                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                                  {ageMismatchLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <span
                          className={cn(
                            'inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors',
                            isPresent
                              ? 'border-emerald-200 bg-emerald-500 text-white'
                              : 'border-slate-200 bg-white text-slate-400'
                          )}
                        >
                          {isPresent ? <CircleCheckBig size={18} /> : <Circle size={18} />}
                        </span>
                      </div>

                      {student.notes && <p className="text-xs text-slate-500">{student.notes}</p>}
                    </Card>
                  </button>
                );
              })
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                Vincule alunos à turma antes de registrar a frequência desta lição.
              </div>
            )}
          </div>

        </div>
      </FullScreenModal>

      <FullScreenModal
        open={studentModal !== null}
        onClose={closeStudentModal}
        title={studentModalTitle}
        subtitle="Alunos vêm do sistema master e aqui são vinculados, ajustados ou transferidos."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeStudentModal}>
              Cancelar
            </Button>
            <Button className="h-12 gap-2" onClick={handleSubmitStudentModal} disabled={isSavingStudent}>
              {studentModal?.mode === 'transfer' ? <ArrowRightLeft size={18} /> : <UserPlus size={18} />}
              {isSavingStudent
                ? 'Salvando...'
                : studentModal?.mode === 'add'
                ? 'Vincular aluno'
                : studentModal?.mode === 'edit'
                  ? 'Salvar vínculo'
                  : 'Confirmar transferência'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {studentModal?.mode === 'add' && (
            <div className="space-y-4 rounded-3xl border border-emerald-100 bg-emerald-50/70 p-4">
              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-white p-1">
                <button
                  type="button"
                  onClick={() => setStudentFormValues((current) => ({ ...current, source: 'member' }))}
                  className={cn(
                    'h-11 rounded-xl text-sm font-semibold transition-colors',
                    studentFormValues.source === 'member' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                  )}
                >
                  Membro existente
                </button>
                <button
                  type="button"
                  onClick={() => setStudentFormValues((current) => ({ ...current, source: 'new', memberIds: [] }))}
                  className={cn(
                    'h-11 rounded-xl text-sm font-semibold transition-colors',
                    studentFormValues.source === 'new' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                  )}
                >
                  Novo aluno
                </button>
              </div>

              {studentFormValues.source === 'member' && (
                <SearchableSelect
                  label="Aluno do sistema master"
                  placeholder="Selecione um membro"
                  searchPlaceholder="Buscar membro..."
                  emptyMessage={isLoadingMemberCandidates ? 'Carregando membros...' : 'Nenhum membro disponível.'}
                  options={masterMemberOptions}
                  value={studentFormValues.memberIds}
                  onChange={handleMemberSelection}
                  multiple={false}
                  closeOnSelect
                />
              )}
            </div>
          )}

          {editingStudent && studentModal?.mode !== 'add' && (
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Aluno selecionado</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{editingStudent.name}</p>
              <p className="text-sm text-slate-500">
                {editingStudent.ageGroup} • Turma atual:{' '}
                {classes.find((item) => item.id === editingStudent.currentClassId)?.title ?? 'Sem turma'}
              </p>
            </div>
          )}

          {(studentModal?.mode === 'add' || studentModal?.mode === 'edit') && (
            <div className="space-y-4">
              {((studentModal?.mode === 'add' && studentFormValues.source === 'new') || studentModal?.mode === 'edit') && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Input
                      label="Nome do aluno"
                      value={studentFormValues.fullName}
                      onChange={(event) =>
                        setStudentFormValues((current) => ({ ...current, fullName: event.target.value }))
                      }
                      placeholder="Nome completo"
                    />
                  </div>
                  <Input
                    label="Nascimento"
                    type="date"
                    value={studentFormValues.birthDate}
                    onChange={(event) =>
                      setStudentFormValues((current) => ({ ...current, birthDate: event.target.value }))
                    }
                  />
                  <Input
                    label="Telefone"
                    value={studentFormValues.phone}
                    onChange={(event) =>
                      setStudentFormValues((current) => ({ ...current, phone: event.target.value }))
                    }
                    placeholder="(00) 00000-0000"
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Responsável"
                      value={studentFormValues.responsibleName}
                      onChange={(event) =>
                        setStudentFormValues((current) => ({ ...current, responsibleName: event.target.value }))
                      }
                      placeholder="Nome do responsável, quando houver"
                    />
                  </div>
                </div>
              )}

              <Input
                label="Data de ingresso na EBD"
                type="date"
                value={studentFormValues.joinedEbdAt}
                onChange={(event) =>
                  setStudentFormValues((current) => ({ ...current, joinedEbdAt: event.target.value }))
                }
              />

              <div className="space-y-1.5">
                <label className="ml-1 text-sm font-medium text-gray-700">Observações do aluno</label>
                <textarea
                  value={studentFormValues.notes}
                  onChange={(event) =>
                    setStudentFormValues((current) => ({ ...current, notes: event.target.value }))
                  }
                  placeholder="Registre informações úteis para o acompanhamento da EBD."
                  className="min-h-[120px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {studentFormError && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">
                  {studentFormError}
                </div>
              )}
            </div>
          )}

          {studentModal?.mode === 'transfer' && (
            <div className="space-y-4">
              <div className="rounded-3xl border border-amber-100 bg-amber-50/80 p-4">
                <div className="mb-2 flex items-center gap-2 text-amber-700">
                  <ArrowRightLeft size={18} />
                  <p className="text-sm font-semibold">Transferência de turma</p>
                </div>
                <p className="text-sm text-amber-900">
                  O histórico anterior será preservado e a nova turma ficará registrada com a data de hoje.
                </p>
              </div>

              <SearchableSelect
                label="Turma de destino"
                placeholder="Selecione a turma"
                searchPlaceholder="Buscar turma..."
                emptyMessage="Nenhuma turma disponível."
                options={targetClassOptions}
                value={studentFormValues.targetClassIds}
                onChange={(values) =>
                  setStudentFormValues((current) => ({ ...current, targetClassIds: values.slice(-1) }))
                }
                multiple={false}
                closeOnSelect
              />
              {studentFormError && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">
                  {studentFormError}
                </div>
              )}
            </div>
          )}
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={isVisitorModalOpen}
        onClose={closeVisitorModal}
        title="Registrar visitante"
        subtitle="Cadastro visual preparado para futura integração por WhatsApp."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeVisitorModal}>
              Cancelar
            </Button>
            <Button className="h-12 gap-2" onClick={handleSaveVisitor}>
              <UserRoundPlus size={18} />
              Registrar visita
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={visitorFormValues.name}
            onChange={(event) => setVisitorFormValues((current) => ({ ...current, name: event.target.value }))}
            placeholder="Nome do visitante"
          />

          <Input
            label="WhatsApp"
            value={visitorFormValues.whatsapp}
            onChange={(event) => setVisitorFormValues((current) => ({ ...current, whatsapp: event.target.value }))}
            placeholder="(00) 00000-0000"
          />

          <div className="rounded-3xl border border-sky-100 bg-sky-50 p-4">
            <p className="text-sm font-semibold text-sky-900">WhatsApp como identificador</p>
            <p className="mt-1 text-sm text-sky-800">
              A duplicidade será tratada na integração. Neste mock, o registro cria a primeira visita de hoje.
            </p>
          </div>
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={historyStudent !== null}
        onClose={() => setHistoryStudentId(null)}
        title="Histórico de turmas"
        subtitle={historyStudent?.name}
      >
        {historyStudent && (
          <div className="space-y-3">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Ingresso na EBD: {formatDateLabel(historyStudent.joinedEbdAt)}
              </p>
            </div>

            {historyStudent.classHistory.length > 0 ? (
              historyStudent.classHistory
                .slice()
                .reverse()
                .map((entry, index) => (
                  <div key={`${entry.classId}-${index}`} className="rounded-3xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-semibold text-slate-900">{entry.className}</p>
                    <p className="mt-1 text-sm text-slate-500">Entrada: {formatDateLabel(entry.enteredAt)}</p>
                    <p className="text-sm text-slate-500">
                      Saída: {entry.leftAt ? formatDateLabel(entry.leftAt) : 'Vínculo ativo'}
                    </p>
                    {entry.reason && <p className="mt-2 text-sm text-slate-600">{entry.reason}</p>}
                  </div>
                ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                Este aluno ainda não possui histórico de turmas registrado.
              </div>
            )}
          </div>
        )}
      </FullScreenModal>

      <FullScreenModal
        open={historyVisitor !== null}
        onClose={() => setVisitorHistoryId(null)}
        title="Histórico de visitas"
        subtitle={historyVisitor?.name}
      >
        {historyVisitor && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Card className="overflow-hidden border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-3">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Visitas
                    </span>
                    <p className="text-3xl font-bold leading-none text-slate-900">{historyVisitor.visits.length}</p>
                  </div>
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
                    <History size={20} />
                  </div>
                </div>
              </Card>
              <Card className="overflow-hidden border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-3">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      WhatsApp
                    </span>
                    <p className="text-sm font-bold leading-tight text-slate-900">{historyVisitor.whatsapp}</p>
                  </div>
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
                    <UserRoundPlus size={20} />
                  </div>
                </div>
              </Card>
            </div>

            {historyVisitor.visits.length > 0 ? (
              historyVisitor.visits
                .slice()
                .sort((left, right) => right.date.localeCompare(left.date))
                .map((visit) => {
                  const lesson = classLessons.find((item) => item.id === visit.lessonId) ?? null;

                  return (
                    <div key={visit.id} className="rounded-3xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            {lesson?.title || `Visita em ${formatDateLabel(visit.date)}`}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {lesson?.lessonNumber ? `Lição ${lesson.lessonNumber} • ` : ''}
                            {formatDateLabel(visit.date)}
                          </p>
                        </div>
                        <span className="rounded-full bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700">
                          Visitante
                        </span>
                      </div>

                      <div className="mt-3 rounded-2xl bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Resumo da visita</p>
                        <p className="mt-1 text-sm text-slate-700">{visit.notes || 'Visita registrada na turma.'}</p>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                Este visitante ainda não possui visitas registradas.
              </div>
            )}
          </div>
        )}
      </FullScreenModal>

      <StudentAttendanceHistoryModal
        open={attendanceHistoryStudent !== null}
        studentName={attendanceHistoryStudent?.name}
        entries={attendanceHistoryEntries}
        summary={attendanceHistorySummary}
        onClose={() => setAttendanceHistoryStudentId(null)}
        formatDateLabel={formatDateLabel}
      />

      <FullScreenModal
        open={historyPersonName !== null}
        onClose={() => setHistoryPersonName(null)}
        title="Histórico da pessoa"
        subtitle={historyPersonName ?? undefined}
      >
        {historyPersonName && (
          <div className="space-y-4">
            {historyPersonStudent && (
              <div className="space-y-3">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Como aluno</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    Ingresso na EBD: {formatDateLabel(historyPersonStudent.joinedEbdAt)}
                  </p>
                </div>

                {historyPersonStudent.classHistory.map((entry, index) => (
                  <div key={`${entry.classId}-${index}`} className="rounded-3xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-semibold text-slate-900">{entry.className}</p>
                    <p className="mt-1 text-sm text-slate-500">Entrada: {formatDateLabel(entry.enteredAt)}</p>
                    <p className="text-sm text-slate-500">
                      Saída: {entry.leftAt ? formatDateLabel(entry.leftAt) : 'Vínculo ativo como aluno'}
                    </p>
                    {entry.reason && <p className="mt-2 text-sm text-slate-600">{entry.reason}</p>}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <div className="rounded-3xl border border-sky-100 bg-sky-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">Como professor</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {historyPersonTeacherClasses.length > 0
                    ? `${historyPersonTeacherClasses.length} turma(s) vinculada(s)`
                    : 'Sem turmas como professor no mock atual'}
                </p>
              </div>

              {teacherParticipationEntries.length > 0 ? (
                teacherParticipationEntries.map((entry) => (
                  <div key={entry.id} className="rounded-3xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{entry.lessonTitle}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {entry.lessonNumber ? `Lição ${entry.lessonNumber} • ` : ''}
                          {formatDateLabel(entry.date)}
                        </p>
                      </div>
                      <span className="rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">
                        Docente
                      </span>
                    </div>
                    <div className="mt-3 rounded-2xl bg-slate-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{entry.className}</p>
                      <p className="mt-1 text-sm text-slate-700">
                        Participação como docente da aula. {entry.presentCount}/{entry.enrolledCount} alunos presentes.
                      </p>
                      {entry.notes && <p className="mt-2 text-sm text-slate-600">{entry.notes}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                  Esta pessoa ainda não possui participação docente registrada no trimestre atual.
                </div>
              )}
            </div>
          </div>
        )}
      </FullScreenModal>

      <FullScreenModal
        open={visitorToConvert !== null}
        onClose={() => setVisitorToConvertId(null)}
        title="Converter visitante em aluno"
        subtitle={visitorToConvert?.name}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={() => setVisitorToConvertId(null)}>
              Cancelar
            </Button>
            <Button className="h-12 gap-2" onClick={handleConfirmConvertVisitor}>
              <UserCheck size={18} />
              Converter
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-900">Pré-matrícula visual</p>
            <p className="mt-1 text-sm text-emerald-800">
              O visitante será adicionado como aluno da turma atual no mock, preservando o WhatsApp nas observações.
            </p>
          </div>

          {visitorToConvert && (
            <div className="rounded-3xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Dados do visitante</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{visitorToConvert.name}</p>
              <p className="text-sm text-slate-500">{visitorToConvert.whatsapp}</p>
              <p className="mt-2 text-sm text-slate-600">
                {visitorToConvert.visits.length} {visitorToConvert.visits.length === 1 ? 'visita registrada' : 'visitas registradas'}
              </p>
            </div>
          )}
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={studentToRemove !== null}
        onClose={() => {
          setStudentToRemoveId(null);
          setStudentRemoveError(null);
        }}
        title="Remover aluno da turma"
        subtitle={studentToRemove?.name}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              onClick={() => {
                setStudentToRemoveId(null);
                setStudentRemoveError(null);
              }}
            >
              Cancelar
            </Button>
            <Button className="h-12 bg-rose-600 hover:bg-rose-700" onClick={handleConfirmRemoveStudent}>
              Remover vínculo
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
          <AlertTriangle size={20} className="shrink-0 text-rose-600" />
          <p className="text-sm text-rose-900">
            O aluno continuará disponível no sistema master, mas ficará sem turma ativa até um novo vínculo.
          </p>
        </div>
        {studentRemoveError && (
          <div className="mt-3 rounded-2xl border border-rose-100 bg-white p-3 text-sm font-medium text-rose-700">
            {studentRemoveError}
          </div>
        )}
      </FullScreenModal>
    </MainLayout>
  );
};

export default ClassStudentsPage;
